import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import PoolMap from '@/components/PoolMap';
import DailyCalendar from '@/components/DailyCalendar';
import LanguageSelector from '@/components/LanguageSelector';
import { LogOut, CheckCircle2, Clock, Users, MapPin, Search, QrCode, CalendarDays, Package, Calendar, ExternalLink, Image, X, Fingerprint, Play, Pause, Square, TableProperties } from 'lucide-react';
import ScheduleTable from '@/components/ScheduleTable';

// ── Color por actividad ────────────────────────────────────────────────────────
function workshopStyle(title) {
  if (title === 'El Club de Manri') return { color: '#A8752B', bg: 'rgba(168,117,43,0.09)', light: 'rgba(168,117,43,0.16)' };
  if (title === 'Minigolf')         return { color: '#2D6A5E', bg: 'rgba(45,106,94,0.08)',  light: 'rgba(45,106,94,0.15)' };
  return                                   { color: '#48293A', bg: 'rgba(72,41,58,0.06)',   light: 'rgba(72,41,58,0.12)' };
}

function OccupancyBar({ current, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const barColor = pct >= 90 ? '#E74C3C' : pct >= 70 ? '#A8752B' : '#27AE60';
  return (
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.08)' }}>
      <div className="h-full rounded-full transition-all" style={{ width: pct + '%', background: barColor }} />
    </div>
  );
}

export default function MonitorDashboard() {
  const { user, logout, api } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [families, setFamilies] = useState([]);
  const [stats, setStats] = useState(null);

  // Checkin-list filters
  const [search, setSearch] = useState('');
  const [filterWorkshop, setFilterWorkshop] = useState('all');
  const [checkinFilterTitle, setCheckinFilterTitle] = useState('all');

  // Workshop-cards filters
  const [wsFilterTitle, setWsFilterTitle] = useState('all');
  const [wsFilterSlot, setWsFilterSlot] = useState('all');

  const [qrToday, setQrToday] = useState(null);
  const [tomorrowWorkshops, setTomorrowWorkshops] = useState([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [tab, setTab] = useState('checkin');
  const [clockStatus, setClockStatus] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [locationModal, setLocationModal] = useState(null);

  // "Hoy"/"mañana" en la zona horaria de Canarias (en-CA → formato YYYY-MM-DD).
  const today    = new Date().toLocaleDateString('en-CA', { timeZone: 'Atlantic/Canary' });
  const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString('en-CA', { timeZone: 'Atlantic/Canary' });

  const fetchData = useCallback(async () => {
    try {
      const [rRes, wRes, fRes, sRes] = await Promise.all([
        api.get('/reservations'),
        api.get('/workshops'),
        api.get('/families'),
        api.get('/stats'),
      ]);
      setReservations(rRes.data);
      setWorkshops(wRes.data);
      setFamilies(fRes.data);
      setStats(sRes.data);
      try { const qtRes = await api.get('/admin/qr-today'); setQrToday(qtRes.data); } catch {}
      try { const tmRes = await api.get(`/workshops/by-date/${tomorrow}`); setTomorrowWorkshops(tmRes.data); } catch {}
      try { const csRes = await api.get('/timeclock/status'); setClockStatus(csRes.data); } catch {}
    } catch { toast.error('Error al cargar datos'); }
  }, [api, tomorrow]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh: el panel queda abierto todo el día en las tablets de los
  // monitores — sin esto, las reservas hechas después de abrirlo no aparecen.
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    const onVisible = () => { if (!document.hidden) fetchData(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData]);

  const handleCheckin = async (id) => {
    try {
      await api.patch(`/reservations/${id}/checkin`);
      toast.success(t('checked_in'));
      fetchData();
    } catch { toast.error('Error en check-in'); }
  };

  const handlePunch = async (type) => {
    setClockLoading(true);
    try {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch {}
      await api.post('/timeclock/punch', { type, lat, lng });
      const csRes = await api.get('/timeclock/status');
      setClockStatus(csRes.data);
      const labels = { clock_in: 'Entrada fichada', clock_out: 'Salida fichada', break_start: 'Pausa iniciada', break_end: 'Pausa finalizada' };
      toast.success(labels[type] || 'Fichado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al fichar'); }
    setClockLoading(false);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  // ── Derived data ─────────────────────────────────────────────────────────────
  const todayWorkshops = workshops
    .filter(w => w.date === today)
    .sort((a, b) => a.time_start.localeCompare(b.time_start));

  const tomorrowSorted = [...tomorrowWorkshops].sort((a, b) => a.time_start.localeCompare(b.time_start));

  // Cards: apply activity + slot filters
  const wsFiltered = todayWorkshops
    .filter(w => wsFilterTitle === 'all' || w.title === wsFilterTitle)
    .filter(w => wsFilterSlot  === 'all' || w.time_slot === wsFilterSlot);

  const morningCards   = wsFiltered.filter(w => w.time_slot === 'morning');
  const afternoonCards = wsFiltered.filter(w => w.time_slot === 'afternoon');

  // Checkin list: apply search + workshop-id + activity-title filters
  const filtered = reservations.filter(r => {
    const matchSearch = search === '' ||
      r.family_name.toLowerCase().includes(search.toLowerCase()) ||
      r.children_names.some(n => n.toLowerCase().includes(search.toLowerCase()));
    const matchWorkshop = filterWorkshop === 'all' || r.workshop_id === filterWorkshop;
    const matchTitle    = checkinFilterTitle === 'all' || r.workshop_title === checkinFilterTitle;
    return matchSearch && matchWorkshop && matchTitle;
  });

  const TABS = [
    { id: 'checkin',    label: t('tab_checkin'),        icon: CheckCircle2 },
    { id: 'horario',    label: t('tab_schedule_today'), icon: Clock },
    { id: 'manana',     label: t('tab_tomorrow'),       icon: CalendarDays },
    { id: 'cuadrante',  label: 'Cuadrante',             icon: TableProperties },
    { id: 'calendario', label: 'Calendario',            icon: Calendar },
    { id: 'fichaje',    label: 'Fichaje',               icon: Fingerprint },
  ];

  const ACTIVITY_TITLES = ['El Club de Manri', 'Minigolf'];

  // ── Shared workshop card (horario / mañana tabs) ───────────────────────────
  function WorkshopRow({ w, wsReservations = [] }) {
    const style = workshopStyle(w.title);
    return (
      <div
        className="p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all rounded-2xl border-l-4"
        style={{ borderColor: style.color, background: style.bg }}
        onClick={() => setSelectedWorkshop(w)}
      >
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: style.light }}>
          <Clock size={18} style={{ color: style.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-sm text-[#1a1a1a]">{w.title}</h3>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: style.light, color: style.color }}>
              {w.registration_type === 'mandatory' ? 'Registro' : 'Libre'}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-0.5 text-xs text-[#4C4D57]">
            <span className="font-medium text-[#1a1a1a]">{w.time_start}–{w.time_end}</span>
            <span>{w.current_count}/{w.max_capacity} {w.capacity_unit === 'palos' ? 'palos' : t('spots')}</span>
            {wsReservations.length > 0 && <span className="text-[#27AE60]">{wsReservations.filter(r => r.checked_in).length} ✓ check-in</span>}
          </div>
        </div>
        {wsReservations.length > 0 && (
          <span className="text-xs font-medium text-[#4C4D57] bg-white/60 px-3 py-1 rounded-full">
            {wsReservations.length} res.
          </span>
        )}
      </div>
    );
  }

  // ── Filter pills helper ────────────────────────────────────────────────────
  function FilterPill({ active, onClick, children }) {
    return (
      <button
        onClick={onClick}
        className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap"
        style={active
          ? { background: '#48293A', color: '#FBF8F3' }
          : { background: 'white', color: '#4C4D57', border: '1px solid rgba(0,0,0,0.12)' }
        }
      >
        {children}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="monitor-dashboard">

      {/* Header */}
      <header className="bg-white border-b border-black/5 px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-xl tracking-tight text-manrique-black flex items-center gap-2">
              <img src="/logo.png" alt="Parque Maritimo" className="h-9 w-auto" />
              <span>{t('monitor_title')}</span>
            </h1>
            <p className="text-xs text-manrique-grey">{t('monitor_subtitle')}</p>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            {qrToday && (
              <div className="flex items-center gap-2 bg-[#48293A]/5 px-4 py-2 rounded-full" data-testid="monitor-qr-badge">
                <QrCode size={16} className="text-[#48293A]" />
                <span className="text-sm font-bold text-[#48293A]">{t('qr_today_label')}: QR-{qrToday.qr_number}</span>
              </div>
            )}
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-manrique-grey hover:text-manrique-black" data-testid="monitor-logout-button">
              <LogOut size={16} /> {t('logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-6">

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="monitor-stats">
            <div className="card-manrique p-5 text-center">
              <p className="font-heading font-bold text-3xl text-manrique-black">{stats.total_reservations}</p>
              <p className="text-sm text-manrique-grey mt-1">{t('reservations_today')}</p>
            </div>
            <div className="card-manrique p-5 text-center">
              <p className="font-heading font-bold text-3xl text-[#27AE60]">{stats.checked_in}</p>
              <p className="text-sm text-manrique-grey mt-1">{t('checkins')}</p>
            </div>
            <div className="card-manrique p-5 text-center">
              <p className="font-heading font-bold text-3xl text-[#48293A]">{todayWorkshops.length}</p>
              <p className="text-sm text-manrique-grey mt-1">Talleres de Hoy</p>
            </div>
            <div className="card-manrique p-5 text-center">
              <p className="font-heading font-bold text-3xl text-manrique-black">{stats.complete_families}</p>
              <p className="text-sm text-manrique-grey mt-1">{t('families')}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="monitor-tabs">
          {TABS.map(ti => {
            const Icon = ti.icon;
            return (
              <button
                key={ti.id}
                onClick={() => setTab(ti.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  tab === ti.id ? 'bg-[#48293A] text-white shadow-lg shadow-[#48293A]/20' : 'bg-white text-manrique-grey border border-black/10 hover:bg-[#FBF8F3]'
                }`}
                data-testid={`monitor-tab-${ti.id}`}
              >
                <Icon size={16} /> {ti.label}
              </button>
            );
          })}
        </div>

        {/* ── TAB: Check-in ───────────────────────────────────────────────────── */}
        {tab === 'checkin' && (
          <div className="space-y-6">

            {/* Talleres del día — filtros + tarjetas */}
            <section>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <h2 className="font-heading font-bold text-lg text-manrique-black">{t('workshops_of_day')}</h2>
                {/* Filtros */}
                <div className="flex gap-2 flex-wrap">
                  <FilterPill active={wsFilterTitle === 'all'} onClick={() => setWsFilterTitle('all')}>Todas</FilterPill>
                  {ACTIVITY_TITLES.map(title => (
                    <FilterPill key={title} active={wsFilterTitle === title} onClick={() => setWsFilterTitle(title)}>
                      {title}
                    </FilterPill>
                  ))}
                  <span className="w-px bg-black/10 mx-1" />
                  <FilterPill active={wsFilterSlot === 'all'}       onClick={() => setWsFilterSlot('all')}>Todo el día</FilterPill>
                  <FilterPill active={wsFilterSlot === 'morning'}   onClick={() => setWsFilterSlot('morning')}>Mañana</FilterPill>
                  <FilterPill active={wsFilterSlot === 'afternoon'} onClick={() => setWsFilterSlot('afternoon')}>Tarde</FilterPill>
                </div>
              </div>

              {wsFiltered.length === 0 ? (
                <div className="card-manrique p-8 text-center text-manrique-muted text-sm">
                  No hay talleres para los filtros seleccionados.
                </div>
              ) : (
                <div className="space-y-5">
                  {[{ label: 'Mañana', items: morningCards }, { label: 'Tarde', items: afternoonCards }].map(group =>
                    group.items.length > 0 && (
                      <div key={group.label}>
                        <p className="text-xs font-bold uppercase tracking-wider text-[#4C4D57] mb-2 pl-1">{group.label}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {group.items.map(w => {
                            const style = workshopStyle(w.title);
                            const wsRes = reservations.filter(r => r.workshop_id === w.id);
                            const checkins = wsRes.filter(r => r.checked_in).length;
                            const unit = w.capacity_unit === 'palos' ? 'palos' : 'plazas';
                            return (
                              <div
                                key={w.id}
                                className="rounded-2xl border-l-4 p-4 cursor-pointer hover:shadow-md transition-all"
                                style={{ borderColor: style.color, background: style.bg }}
                                onClick={() => setSelectedWorkshop(w)}
                                data-testid={`monitor-workshop-${w.id}`}
                              >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <span className="font-heading font-bold text-sm leading-tight" style={{ color: style.color }}>{w.title}</span>
                                  <button
                                    onClick={e => { e.stopPropagation(); setFilterWorkshop(filterWorkshop === w.id ? 'all' : w.id); }}
                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 transition-all"
                                    style={filterWorkshop === w.id
                                      ? { background: style.color, color: 'white' }
                                      : { background: style.light, color: style.color }
                                    }
                                    data-testid={`filter-workshop-${w.id}`}
                                  >
                                    {filterWorkshop === w.id ? t('filtered') : t('filter_btn')}
                                  </button>
                                </div>

                                <div className="flex items-center gap-1 text-xs text-[#4C4D57] mb-3">
                                  <Clock size={11} /> {w.time_start}–{w.time_end}
                                </div>

                                <div className="space-y-1.5">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-[#4C4D57]">
                                      {w.current_count} / {w.max_capacity} {unit}
                                    </span>
                                    <span className="font-semibold" style={{ color: '#27AE60' }}>{checkins} check-in</span>
                                  </div>
                                  <OccupancyBar current={w.current_count} max={w.max_capacity} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </section>

            {/* Lista de reservas / Check-in */}
            <section>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h2 className="font-heading font-bold text-lg text-manrique-black">
                    Reservas {filterWorkshop !== 'all' && `— ${workshops.find(w => w.id === filterWorkshop)?.title ?? ''}`}
                  </h2>
                  {/* Filtro por actividad */}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <FilterPill active={checkinFilterTitle === 'all'} onClick={() => setCheckinFilterTitle('all')}>Todas</FilterPill>
                    {ACTIVITY_TITLES.map(title => (
                      <FilterPill key={title} active={checkinFilterTitle === title} onClick={() => setCheckinFilterTitle(title)}>
                        {title}
                      </FilterPill>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-manrique-muted" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('search_family')}
                    className="input-manrique pl-10 w-64"
                    data-testid="monitor-search-input"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {filtered.length === 0 ? (
                  <div className="card-manrique p-8 text-center text-manrique-muted">
                    {t('no_reservations_filter')}{filterWorkshop !== 'all' ? ` ${t('no_reservations_workshop')}` : ''}
                  </div>
                ) : (
                  filtered.map(r => {
                    const style = workshopStyle(r.workshop_title);
                    return (
                      <div key={r.id} className="card-manrique p-5 flex items-center justify-between flex-wrap gap-4" data-testid={`monitor-reservation-${r.id}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${r.checked_in ? 'bg-[#27AE60]/10' : 'bg-[#FBF8F3]'}`}>
                            {r.checked_in ? <CheckCircle2 size={20} className="text-[#27AE60]" /> : <Users size={20} className="text-manrique-muted" />}
                          </div>
                          <div>
                            <p className="font-medium text-manrique-black">{r.family_name}</p>
                            <p className="text-sm text-manrique-grey">
                              <span className="font-semibold" style={{ color: style.color }}>{r.workshop_title}</span>
                              {' — '}{r.children_names.join(', ')}
                            </p>
                            {r.pool_location && (
                              <button
                                onClick={() => setLocationModal(r.pool_location)}
                                className="text-xs text-[#48293A] flex items-center gap-1 mt-0.5 hover:underline"
                                data-testid={`location-btn-${r.id}`}
                              >
                                <MapPin size={11} /> Ver ubicación
                              </button>
                            )}
                          </div>
                        </div>
                        {!r.checked_in ? (
                          <button onClick={() => handleCheckin(r.id)} className="btn-manrique text-sm py-2" data-testid={`checkin-button-${r.id}`}>
                            Check-in
                          </button>
                        ) : (
                          <span className="text-sm font-medium text-[#27AE60] bg-[#27AE60]/5 px-4 py-2 rounded-full">{t('present')}</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )}

        {/* ── TAB: Horario Hoy ────────────────────────────────────────────────── */}
        {tab === 'horario' && (
          <div className="space-y-4" data-testid="monitor-horario-tab">
            <h2 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2">
              <Calendar size={20} /> {t('activities_today')} — {today}
            </h2>
            {todayWorkshops.length === 0 ? (
              <div className="card-manrique p-10 text-center text-manrique-muted">
                <Clock size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t('no_activities_today')}</p>
              </div>
            ) : (
              [{ label: 'Mañana', items: todayWorkshops.filter(w => w.time_slot === 'morning') },
               { label: 'Tarde',  items: todayWorkshops.filter(w => w.time_slot === 'afternoon') }].map(group =>
                group.items.length > 0 && (
                  <div key={group.label}>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#4C4D57] mb-2 pl-1">{group.label}</p>
                    <div className="space-y-2">
                      {group.items.map(w => (
                        <WorkshopRow key={w.id} w={w} wsReservations={reservations.filter(r => r.workshop_id === w.id)} />
                      ))}
                    </div>
                  </div>
                )
              )
            )}
          </div>
        )}

        {/* ── TAB: Mañana ─────────────────────────────────────────────────────── */}
        {tab === 'manana' && (
          <div className="space-y-4" data-testid="monitor-manana-tab">
            <h2 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2">
              <CalendarDays size={20} /> {t('activities_tomorrow')} — {tomorrow}
            </h2>
            {tomorrowSorted.length === 0 ? (
              <div className="card-manrique p-10 text-center text-manrique-muted">
                <CalendarDays size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t('no_activities_tomorrow')}</p>
              </div>
            ) : (
              [{ label: 'Mañana', items: tomorrowSorted.filter(w => w.time_slot === 'morning') },
               { label: 'Tarde',  items: tomorrowSorted.filter(w => w.time_slot === 'afternoon') }].map(group =>
                group.items.length > 0 && (
                  <div key={group.label}>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#4C4D57] mb-2 pl-1">{group.label}</p>
                    <div className="space-y-2">
                      {group.items.map(w => <WorkshopRow key={w.id} w={w} />)}
                    </div>
                  </div>
                )
              )
            )}
          </div>
        )}

        {/* ── TAB: Cuadrante semanal ──────────────────────────────────────────── */}
        {tab === 'cuadrante' && (
          <div data-testid="monitor-cuadrante-tab">
            <h2 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2 mb-5">
              <TableProperties size={20} /> Cuadrante semanal de monitores
            </h2>
            <ScheduleTable editable={false} />
          </div>
        )}

        {/* ── TAB: Calendario ─────────────────────────────────────────────────── */}
        {tab === 'calendario' && (
          <div data-testid="monitor-calendario-tab">
            <h2 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2 mb-5">
              <Calendar size={20} /> Calendario de Actividades
            </h2>
            <DailyCalendar api={api} />
          </div>
        )}

        {/* ── TAB: Fichaje ─────────────────────────────────────────────────────── */}
        {tab === 'fichaje' && clockStatus && (
          <div className="space-y-6" data-testid="monitor-fichaje-tab">
            <h2 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2">
              <Fingerprint size={20} /> Fichaje Laboral — {clockStatus.date}
            </h2>
            <div className="card-manrique p-6 text-center space-y-5">
              <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold ${
                clockStatus.status === 'working'     ? 'bg-[#27AE60]/10 text-[#27AE60]' :
                clockStatus.status === 'on_break'    ? 'bg-[#A8752B]/10 text-[#A8752B]' :
                clockStatus.status === 'finished'    ? 'bg-[#48293A]/10 text-[#48293A]' :
                'bg-[#FBF8F3] text-manrique-grey'
              }`}>
                {clockStatus.status === 'working'     && 'Trabajando'}
                {clockStatus.status === 'on_break'    && 'En pausa'}
                {clockStatus.status === 'finished'    && 'Jornada finalizada'}
                {clockStatus.status === 'not_started' && 'Sin fichar'}
              </div>
              <div className="flex justify-center gap-3 flex-wrap">
                {clockStatus.status === 'not_started' && (
                  <button onClick={() => handlePunch('clock_in')} disabled={clockLoading} className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#27AE60] text-white font-bold text-base shadow-lg hover:shadow-xl transition-all" data-testid="punch-clock-in">
                    <Play size={20} /> Fichar Entrada
                  </button>
                )}
                {clockStatus.status === 'working' && (
                  <>
                    {clockStatus.breaks_enabled && (
                      <button onClick={() => handlePunch('break_start')} disabled={clockLoading} className="flex items-center gap-2 px-6 py-4 rounded-2xl bg-[#A8752B] text-white font-bold shadow-lg hover:shadow-xl transition-all" data-testid="punch-break-start">
                        <Pause size={18} /> Iniciar Pausa
                      </button>
                    )}
                    <button onClick={() => handlePunch('clock_out')} disabled={clockLoading} className="flex items-center gap-2 px-6 py-4 rounded-2xl bg-[#48293A] text-white font-bold shadow-lg hover:shadow-xl transition-all" data-testid="punch-clock-out">
                      <Square size={18} /> Fichar Salida
                    </button>
                  </>
                )}
                {clockStatus.status === 'on_break' && (
                  <button onClick={() => handlePunch('break_end')} disabled={clockLoading} className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#27AE60] text-white font-bold text-base shadow-lg hover:shadow-xl transition-all" data-testid="punch-break-end">
                    <Play size={20} /> Reanudar Trabajo
                  </button>
                )}
              </div>
            </div>
            {clockStatus.punches && clockStatus.punches.length > 0 && (
              <div className="card-manrique p-5">
                <h3 className="font-heading font-bold text-sm text-manrique-black mb-3">Registro de Hoy</h3>
                <div className="space-y-2">
                  {clockStatus.punches.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className={`w-2.5 h-2.5 rounded-full ${p.type === 'clock_in' ? 'bg-[#27AE60]' : p.type === 'clock_out' ? 'bg-[#48293A]' : p.type === 'break_start' ? 'bg-[#A8752B]' : 'bg-[#27AE60]'}`} />
                      <span className="font-mono font-bold text-manrique-black">{p.time?.slice(0, 5)}</span>
                      <span className="text-manrique-grey">
                        {p.type === 'clock_in' && 'Entrada'}{p.type === 'clock_out' && 'Salida'}{p.type === 'break_start' && 'Inicio pausa'}{p.type === 'break_end' && 'Fin pausa'}
                      </span>
                      {p.lat && <span className="text-xs text-manrique-muted ml-auto">GPS</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-manrique-muted text-center">Registro conforme al RD 8/2019. Los datos se conservan 4 años.</p>
          </div>
        )}

      </main>

      {/* ── Modal detalle taller ──────────────────────────────────────────────── */}
      {selectedWorkshop && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedWorkshop(null)} data-testid="workshop-detail-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="rounded-t-3xl px-6 pt-6 pb-4" style={{ background: workshopStyle(selectedWorkshop.title).color }}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">{selectedWorkshop.title}</h3>
                  <p className="text-white/80 text-sm mt-1">{selectedWorkshop.date} | {selectedWorkshop.time_start}–{selectedWorkshop.time_end}</p>
                </div>
                <button onClick={() => setSelectedWorkshop(null)} className="p-1.5 hover:bg-white/20 rounded-full transition-colors" data-testid="close-workshop-modal">
                  <X size={20} className="text-white" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-white/20 text-white">
                  {selectedWorkshop.registration_type === 'free' ? t('free_access') : t('mandatory_registration')}
                </span>
                <span className="text-[11px] font-medium px-3 py-1 rounded-full bg-white/20 text-white">
                  {selectedWorkshop.age_min}–{selectedWorkshop.age_max} {t('years_old')}
                </span>
                <span className="text-[11px] font-medium px-3 py-1 rounded-full bg-white/20 text-white">
                  {selectedWorkshop.current_count ?? 0}/{selectedWorkshop.max_capacity} {selectedWorkshop.capacity_unit === 'palos' ? 'palos' : t('spots')}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-manrique-muted mb-2">{t('description_label')}</h4>
                <p className="text-sm text-manrique-black leading-relaxed">{selectedWorkshop.description || t('no_description')}</p>
              </div>
              <div className="bg-[#A8752B]/5 border border-[#A8752B]/15 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Package size={15} className="text-[#A8752B]" />
                  <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-[#A8752B]">Materiales necesarios</h4>
                </div>
                <p className="text-sm text-manrique-black">{selectedWorkshop.materials || t('not_specified')}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={15} className="text-[#48293A]" />
                  <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-manrique-muted">
                    {t('location_label')}: {selectedWorkshop.pool_location
                      ? (selectedWorkshop.pool_location.zone === 'libre' ? t('pool_free') : `${t('pool_zone')} ${selectedWorkshop.pool_location.zone}`)
                      : t('not_assigned')}
                  </h4>
                </div>
                <PoolMap value={selectedWorkshop.pool_location ?? null} onChange={() => {}} readOnly />
              </div>
              <div className="flex flex-col gap-4">
                {selectedWorkshop.drive_url ? (
                  <a href={selectedWorkshop.drive_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-[#48293A]/5 border border-[#48293A]/15 rounded-2xl p-4 hover:bg-[#48293A]/10 transition-colors group"
                    data-testid="workshop-drive-link">
                    <div className="w-10 h-10 rounded-xl bg-[#48293A]/10 flex items-center justify-center flex-shrink-0">
                      <ExternalLink size={18} className="text-[#48293A]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-[#48293A]">{t('open_drive')}</h4>
                      <p className="text-xs text-manrique-muted">{t('drive_resources')}</p>
                    </div>
                    <ExternalLink size={14} className="text-manrique-muted group-hover:text-[#48293A]" />
                  </a>
                ) : (
                  <div className="flex items-center gap-3 bg-[#FBF8F3] border border-black/5 rounded-2xl p-4 opacity-50">
                    <div className="w-10 h-10 rounded-xl bg-black/5 flex items-center justify-center flex-shrink-0">
                      <ExternalLink size={18} className="text-manrique-muted" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-manrique-grey">{t('google_drive')}</h4>
                      <p className="text-xs text-manrique-muted">{t('drive_not_configured')}</p>
                    </div>
                  </div>
                )}
                {selectedWorkshop.images?.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Image size={15} className="text-manrique-grey" />
                      <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-manrique-muted">{t('photos')} ({selectedWorkshop.images.length})</h4>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedWorkshop.images.map((img, i) => (
                        <div key={i} className="rounded-2xl overflow-hidden border border-black/5 aspect-video">
                          <img src={img} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-[#FBF8F3] border border-black/5 rounded-2xl p-4 opacity-50">
                    <div className="w-10 h-10 rounded-xl bg-black/5 flex items-center justify-center flex-shrink-0">
                      <Image size={18} className="text-manrique-muted" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-manrique-grey">{t('photos')}</h4>
                      <p className="text-xs text-manrique-muted">{t('no_photos')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal ubicación familia ───────────────────────────────────────────── */}
      {locationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setLocationModal(null)} data-testid="location-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2">
                <MapPin size={18} className="text-[#48293A]" /> Ubicación en la piscina
              </h3>
              <button onClick={() => setLocationModal(null)} className="p-1.5 hover:bg-[#FBF8F3] rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <PoolMap value={locationModal} onChange={() => {}} readOnly />
          </div>
        </div>
      )}
    </div>
  );
}
